/**
 * Nuance Picker - Web Component for coloration products
 * Manages search, filters, variant selection with quantities, and cart.
 * All rendering is done server-side in Liquid — JS handles interactivity only.
 *
 * Note: x-drawer teleports itself out of the nuance-picker element when opened,
 * so we reference drawer elements via document.getElementById(drawerId).
 */

if (!window.customElements.get('nuance-picker')) {
  class NuancePicker extends HTMLElement {
    constructor() {
      super();
      this.selections = new Map(); // variantId -> quantity
      this.variants = [];
      this.t = {};
    }

    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-nuance-variants]').textContent);
      this.t = JSON.parse(this.querySelector('[data-nuance-translations]').textContent);
      this.drawerId = this.getAttribute('drawer-id');
      this.currentValueEl = this.querySelector('.nuance-picker__current-value');

      this._bindEvents();
    }

    /** Lazily resolve drawer elements (drawer may teleport on open) */
    get drawerEl() {
      return this._drawerEl || (this._drawerEl = document.getElementById(this.drawerId));
    }

    _q(selector) {
      return this.drawerEl?.querySelector(selector) || this.querySelector(selector);
    }

    _qAll(selector) {
      const fromDrawer = this.drawerEl?.querySelectorAll(selector);
      if (fromDrawer && fromDrawer.length) return fromDrawer;
      return this.querySelectorAll(selector);
    }

    _bindEvents() {
      // Search — bind on first drawer open since elements may not exist yet
      const triggerBtn = this.querySelector('.nuance-picker__trigger-button');
      let bound = false;

      const bindDrawerEvents = () => {
        if (bound) return;
        // Small delay to let drawer teleport
        requestAnimationFrame(() => {
          this._drawerEl = null; // reset cache
          const searchInput = this._q('[data-nuance-search]');
          if (searchInput) {
            searchInput.addEventListener('input', () => this._applyFilters());
          }

          // Filter buttons (delegated)
          this._qAll('[data-filter]').forEach(group => {
            group.addEventListener('click', (e) => {
              const btn = e.target.closest('[data-filter-value]');
              if (!btn) return;
              group.querySelectorAll('.nuance-picker__filter-btn').forEach(b => b.classList.remove('is-active'));
              btn.classList.add('is-active');
              this._applyFilters();
            });
          });

          // Quantity buttons in grid (delegated)
          const grid = this._q('[data-nuance-grid]');
          if (grid) {
            grid.addEventListener('click', (e) => {
              const btn = e.target.closest('[data-action]');
              if (!btn) return;
              const item = btn.closest('.nuance-picker__item');
              const variantId = parseInt(item.dataset.variantId);
              this._updateQuantity(variantId, btn.dataset.action);
            });

            // Direct input editing
            grid.addEventListener('change', (e) => {
              const input = e.target.closest('[data-qty-input]');
              if (!input) return;
              const item = input.closest('.nuance-picker__item');
              const variantId = parseInt(item.dataset.variantId);
              const stock = parseInt(item.dataset.stock) || 0;
              let val = parseInt(input.value) || 0;
              val = Math.max(0, Math.min(val, stock));
              this._setQuantity(variantId, val);
            });
          }

          // Clear all
          const clearBtn = this._q('[data-clear-all]');
          if (clearBtn) {
            clearBtn.addEventListener('click', () => this._clearAll());
          }

          // Add to cart
          const addBtn = this._q('[data-add-to-cart]');
          if (addBtn) {
            addBtn.addEventListener('click', () => this._addToCart());
          }

          bound = true;
        });
      };

      if (triggerBtn) {
        triggerBtn.addEventListener('click', bindDrawerEvents, { once: true });
      }
    }

    _applyFilters() {
      const searchInput = this._q('[data-nuance-search]');
      const searchTerm = (searchInput?.value || '').toLowerCase().trim();

      const activeCatBtn = this._q('[data-filter="categorie"] .is-active');
      const activeTonBtn = this._q('[data-filter="tonalite"] .is-active');
      const activeCategory = activeCatBtn?.dataset.filterValue || 'all';
      const activeTonalite = activeTonBtn?.dataset.filterValue || 'all';

      let visibleCount = 0;
      const items = this._qAll('.nuance-picker__item');

      for (const item of items) {
        const title = item.dataset.variantTitle.toLowerCase();
        const categories = item.dataset.categories || '';
        const tonalites = item.dataset.tonalites || '';

        let show = true;

        if (searchTerm && !title.includes(searchTerm)) {
          show = false;
        }

        if (show && activeCategory !== 'all') {
          const catList = categories.split(' ');
          if (!catList.includes(activeCategory)) {
            show = false;
          }
        }

        if (show && activeTonalite !== 'all') {
          const tonList = tonalites.split(' ');
          if (!tonList.includes(activeTonalite)) {
            show = false;
          }
        }

        item.hidden = !show;
        if (show) visibleCount++;
      }

      const countEl = this._q('[data-nuance-count]');
      if (countEl) {
        countEl.textContent = this._pluralize(visibleCount, this.t.nuances_found_one, this.t.nuances_found_other);
      }
    }

    _setQuantity(variantId, qty) {
      const variant = this.variants.find(v => v.id === variantId);
      if (!variant) return;

      // Clamp to stock
      qty = Math.max(0, Math.min(qty, variant.stock));

      if (qty === 0) {
        this.selections.delete(variantId);
      } else {
        this.selections.set(variantId, qty);
      }

      this._updateItemUI(variantId, qty);
      this._updateFooter();
      this._updateCurrentValue();
    }

    _updateQuantity(variantId, action) {
      const currentQty = this.selections.get(variantId) || 0;
      const variant = this.variants.find(v => v.id === variantId);
      if (!variant) return;

      let newQty;
      if (action === 'plus') {
        newQty = Math.min(currentQty + 1, variant.stock);
      } else {
        newQty = Math.max(0, currentQty - 1);
      }

      this._setQuantity(variantId, newQty);
    }

    _updateItemUI(variantId, qty) {
      const item = this._q(`[data-variant-id="${variantId}"]`);
      if (!item) return;

      const input = item.querySelector('[data-qty-input]');
      const minusBtn = item.querySelector('[data-action="minus"]');
      const plusBtn = item.querySelector('[data-action="plus"]');
      const qtyContainer = item.querySelector('.nuance-picker__item-qty');
      const stock = parseInt(item.dataset.stock) || 0;

      input.value = qty;
      minusBtn.disabled = qty <= 0;
      plusBtn.disabled = !item.dataset.available || item.dataset.available === 'false' || qty >= stock;

      if (qty > 0) {
        qtyContainer.classList.add('has-quantity');
      } else {
        qtyContainer.classList.remove('has-quantity');
      }
    }

    _updateFooter() {
      const totalArticles = this.selections.size;
      let totalUnits = 0;
      let totalPrice = 0;

      this.selections.forEach((qty, variantId) => {
        const variant = this.variants.find(v => v.id === variantId);
        if (variant) {
          totalUnits += qty;
          totalPrice += variant.price * qty;
        }
      });

      const countEl = this._q('[data-selection-count]');
      const articlesEl = this._q('[data-total-articles]');
      const unitsEl = this._q('[data-total-units]');
      const priceEl = this._q('[data-total-price]');
      const addBtn = this._q('[data-add-to-cart]');

      if (countEl) countEl.textContent = totalArticles;
      if (articlesEl) articlesEl.textContent = this._pluralize(totalArticles, this.t.articles_one, this.t.articles_other);
      if (unitsEl) unitsEl.textContent = this._pluralize(totalUnits, this.t.units_one, this.t.units_other);
      if (priceEl) priceEl.textContent = this._formatMoney(totalPrice);

      if (addBtn) {
        addBtn.disabled = totalArticles === 0;
        const labelFull = addBtn.querySelector('.nuance-picker__btn-label-full');
        const labelShort = addBtn.querySelector('.nuance-picker__btn-label-short');
        const text = this._replacePlaceholder(this.t.add_to_cart, totalArticles);
        const textShort = this._replacePlaceholder(this.t.add_to_cart_short, totalArticles);
        if (labelFull) { labelFull.textContent = text; } else { addBtn.textContent = text; }
        if (labelShort) labelShort.textContent = textShort;
      }

      this._renderSelectionList();
    }

    _renderSelectionList() {
      const selectionList = this._q('[data-selection-list]');
      if (!selectionList) return;

      const items = [];

      this.selections.forEach((qty, variantId) => {
        const variant = this.variants.find(v => v.id === variantId);
        if (!variant) return;

        const lineTotal = variant.price * qty;
        const imageHtml = variant.image
          ? `<img src="${variant.image}" alt="${this._escapeHtml(variant.title)}" class="nuance-picker__selection-item-image" width="32" height="32" loading="lazy">`
          : `<div class="nuance-picker__selection-item-image"></div>`;

        items.push(`
          <div class="nuance-picker__selection-item" data-selection-variant="${variantId}">
            ${imageHtml}
            <div class="nuance-picker__selection-item-info">
              <span class="nuance-picker__selection-item-title">${variant.price_formatted}</span>
              <span class="nuance-picker__selection-item-title">${this._escapeHtml(variant.title)}</span>
            </div>
            <div class="nuance-picker__selection-item-actions">
              <div class="nuance-picker__item-qty has-quantity">
                <button type="button" class="nuance-picker__qty-btn" data-selection-action="minus" data-variant-id="${variantId}" aria-label="${this._escapeHtml(this.t.decrease_quantity)}">-</button>
                <input type="number" class="nuance-picker__qty-input" value="${qty}" min="0" max="${variant.stock}" data-selection-qty-input data-variant-id="${variantId}">
                <button type="button" class="nuance-picker__qty-btn" data-selection-action="plus" data-variant-id="${variantId}" aria-label="${this._escapeHtml(this.t.increase_quantity)}" ${qty >= variant.stock ? 'disabled' : ''}>+</button>
              </div>
              <span class="nuance-picker__selection-item-price">${this._formatMoney(lineTotal)}</span>
              <button type="button" class="nuance-picker__delete-btn" data-selection-delete="${variantId}" aria-label="${this._escapeHtml(this.t.delete)}">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </div>
        `);
      });

      selectionList.innerHTML = items.join('');

      // Bind +/- in selection list
      selectionList.querySelectorAll('[data-selection-action]').forEach(btn => {
        btn.addEventListener('click', () => {
          const variantId = parseInt(btn.dataset.variantId);
          this._updateQuantity(variantId, btn.dataset.selectionAction);
        });
      });

      // Bind direct input in selection list
      selectionList.querySelectorAll('[data-selection-qty-input]').forEach(input => {
        input.addEventListener('change', () => {
          const variantId = parseInt(input.dataset.variantId);
          const variant = this.variants.find(v => v.id === variantId);
          let val = parseInt(input.value) || 0;
          val = Math.max(0, Math.min(val, variant ? variant.stock : 0));
          this._setQuantity(variantId, val);
        });
      });

      // Bind delete
      selectionList.querySelectorAll('[data-selection-delete]').forEach(btn => {
        btn.addEventListener('click', () => {
          const variantId = parseInt(btn.dataset.selectionDelete);
          this._setQuantity(variantId, 0);
        });
      });
    }

    _updateCurrentValue() {
      if (!this.currentValueEl) return;

      if (this.selections.size === 0) {
        this.currentValueEl.textContent = this.t.not_selected;
      } else if (this.selections.size === 1) {
        const [variantId] = this.selections.keys();
        const variant = this.variants.find(v => v.id === variantId);
        this.currentValueEl.textContent = variant ? variant.title : this.t.not_selected;
      } else {
        this.currentValueEl.textContent = `${this.selections.size} ${this.t.nuances_selected.toLowerCase()}`;
      }
    }

    _clearAll() {
      const previousSelections = [...this.selections.keys()];
      this.selections.clear();
      previousSelections.forEach(variantId => this._updateItemUI(variantId, 0));
      this._updateFooter();
      this._updateCurrentValue();
    }

    async _addToCart() {
      if (this.selections.size === 0) return;

      const addBtn = this._q('[data-add-to-cart]');
      if (addBtn) {
        addBtn.setAttribute('aria-busy', 'true');
        const labelFull = addBtn.querySelector('.nuance-picker__btn-label-full');
        const labelShort = addBtn.querySelector('.nuance-picker__btn-label-short');
        if (labelFull) { labelFull.textContent = this.t.adding_to_cart; } else { addBtn.textContent = this.t.adding_to_cart; }
        if (labelShort) labelShort.textContent = this.t.adding_to_cart;
      }

      const items = [];
      this.selections.forEach((qty, variantId) => {
        items.push({ id: variantId, quantity: qty });
      });

      try {
        const sectionsToBundle = new Set();
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const formData = {
          items: items,
          sections: [...sectionsToBundle].join(',')
        };

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        const responseJson = await response.json();
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent }
        }));

        const drawer = this.drawerEl;
        if (drawer && drawer.hide) {
          drawer.hide();
        }

        if (window.themeVariables.settings.cartType === 'page') {
          window.location.href = `${Shopify.routes.root}cart`;
        } else if (window.themeVariables.settings.cartType === 'message') {
          const msg = this._pluralize(items.length, this.t.added_to_cart_one, this.t.added_to_cart_other);
          document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: msg } }));
        }

        this._clearAll();
      } catch (error) {
        console.error('Nuance picker: Error adding to cart', error);
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message: this.t.cart_error }
        }));
      } finally {
        if (addBtn) {
          addBtn.removeAttribute('aria-busy');
        }
        this._updateFooter();
      }
    }

    // -- Helpers --

    _escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return `€${amount}`;
    }

    /** Replace {{ count }} in a translated string */
    _replacePlaceholder(str, count) {
      return (str || '').replace(/__count__/g, count);
    }

    /** Pick singular/plural form and replace {{ count }} */
    _pluralize(count, one, other) {
      const template = count === 1 ? one : other;
      return this._replacePlaceholder(template, count);
    }
  }

  window.customElements.define('nuance-picker', NuancePicker);
}
